import { getTheme, themed, Box, Div, Flow, Text as Tx, type ColorOption, } from "@bracket/ui";
import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { Typography } from "../Typography";
import { Button } from "../Button";

// Define the animation keyframes
const blinkRowAnimation = keyframes`
  0%, 100% {
    background: transparent;
    box-shadow: none;
    opacity: 1;
  }
  50% {
    background: rgba(3, 255, 143, 0.5);
  }
`;

export type ColumnDefinition<T, PK extends string = "id"> = {
	header: string | string[];
	accessor: keyof T | ((item: T) => string|number|Partial<T>);
	render?: (value: ReturnType<typeof this.accessor>, item: T) => JSX.Element;
	width?: string;
};

export type TableProps<T extends { [key in I]: string | number }, I extends string = "id"> = {
	data: T[];
	/** Column definitions specifying how to display each column, when null, the value is skipped */
	columns: Array<ColumnDefinition<T>|null>;
	/** Whether to highlight rows when their data updates */
	highlightOnUpdate?: boolean;
	/** Number of items to show per page, enables pagination if set */
	paginate?: number;
	/** Color override for the table header text */
	headerColor?: ColorOption;
	/** Whether to show separator line below header row */
	showHeaderSeparator?: boolean;
	/** Custom styles to apply to the table container */
	tableContainerStyle?: React.CSSProperties;
	/** Whether to show separator line below the last row */
	showLastRowSeparator?: boolean;
	pk?: I;
};

const TableContainer = styled(Div)`
	width: 100%;
	overflow-x: auto;
	background: ${() => getTheme().surface_container_lowest};
	border-radius: 0.5rem;
	padding: 1.25rem;
`;

const StyledTable = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const TableHeader = styled.thead<{ $showSeparator: boolean }>`
	border-bottom: ${({ $showSeparator }) => $showSeparator ? "1px solid var(--outline-variant)" : "none"};
`;

const TableHeaderCell = styled.th<{ $width?: string }>`
	padding: 1.25rem 1.125rem;
	text-align: left;
	color: ${() => getTheme().subheading};
	width: ${({ $width }) => $width ? $width : "auto"};
`;

const TableRow = styled.tr<{
	$highlight: boolean;
	$showLastRowSeparator: boolean;
}>`
	border-bottom: 1px solid var(--outline-variant);

	${({ $highlight }) =>
		$highlight &&
		css`
			animation: ${blinkRowAnimation} 1.5s ease;
		`}

	&:last-child {
		border-bottom: ${({ $showLastRowSeparator }) => $showLastRowSeparator === false && "none"};
	}
`;

const TableCell = styled.td<{ $width?: string }>`
	padding: 1.25rem 1.125rem;
	color: ${() => getTheme().paragraph};
	width: ${({ $width }) => $width ? $width : "auto"};
`;

const PaginationContainer = styled(Box)`
	justify-content: flex-end;
	align-items: center;
	margin-top: 1rem;
`;

const PaginationButtonContainer = styled(Box)`
	gap: 1rem;
	margin-left: auto;
`;

const ensureReactNode = <T extends { [key in I]: string | number }, I extends string = "id">(content: JSX.Element|string|number|boolean|Partial<T>): React.ReactNode => {
	if(React.isValidElement(content)) {
		return content;
	}
	if (
		typeof content === "string" ||
		typeof content === "number" ||
		typeof content === "boolean"
	) {
		return content.toString();
	}
	return JSON.stringify(content);
};

type PaginationProps = {
	currentPage: number;
	totalPages: number;
	itemsPerPage: number;
	totalItems: number;
	onPageChange: (page: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
	currentPage,
	totalPages,
	itemsPerPage,
	totalItems,
	onPageChange,
}) => {
	const startItem = (currentPage - 1) * itemsPerPage + 1;
	const endItem = Math.min(currentPage * itemsPerPage, totalItems);

	return (
		<PaginationContainer>
			<Tx sm color="paragraph">Showing {startItem} to {endItem} of {totalItems} results</Tx>
			<PaginationButtonContainer>
				<Button label="Previous" disabled={currentPage === 1} variant="text" onClick={() => onPageChange(currentPage - 1)} />
				<Button label="Next" disabled={currentPage === totalPages} variant="text" onClick={() => onPageChange(currentPage + 1)} />
			</PaginationButtonContainer>
		</PaginationContainer>
	);
};

export function Table<T extends { [key in I]: string | number }, I extends string = "id">({
	data,
	columns,
	highlightOnUpdate,
	paginate,
	headerColor,
	showHeaderSeparator = false,
	tableContainerStyle,
	showLastRowSeparator = true,
	pk = "id" as I,
}: TableProps<T, I>) {
	themed();

	const [newIds, setNewIds] = useState<Set<string | number>>(new Set());
	const prevDataIds = useRef<Set<string | number>>(new Set());
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		if(!highlightOnUpdate || !data?.length || !data[0]?.[pk]) {
			return;
		}

		const currentIds = new Set(data.map((d) => d[pk]));
		const newItemIds = new Set(
			Array.from(currentIds).filter(_ => !prevDataIds.current.has(pk))
		);
		setNewIds(newItemIds);
		prevDataIds.current = currentIds;
	}, [data, highlightOnUpdate]);

	const itemsPerPage = paginate || data.length;
	const totalPages = Math.ceil(data.length / itemsPerPage);
	const paginatedData = paginate
		? data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
		: data;

	return (
		<TableContainer style={tableContainerStyle}>
			<StyledTable>
				<TableHeader $showSeparator={showHeaderSeparator}>
					<tr>
					{
						columns.map((column, index) => {
							if(column === null) {
								return null;
							}
							return (
								<TableHeaderCell key={`${column.header}-${index}`} $width={column.width}>
									<Tx sm color={headerColor || "heading"} weight="500">
									{
										Array.isArray(column.header) ? column.header.map((h, i) => (<ColumnHeaderSeg key={`${h}-${i}`}>{h}<br key={`${h}-${i}`} /></ColumnHeaderSeg>)) : column.header
									}
									</Tx>
								</TableHeaderCell>
							);
						})
					}
					</tr>
				</TableHeader>
				<tbody>
					{
						paginatedData.map((item, idx) => {
							return (
								<TableRow key={`${item[pk]}-tr-${idx}`} $highlight={newIds.has(item[pk])} $showLastRowSeparator={showLastRowSeparator}>
								{
									columns.map((column, cellIndex) => {
										if(column === null) {
											return null;
										}
										return(
											<TableCell key={`${column.header}-tc-${cellIndex}`} $width={column.width}>
											{
												column.render ? (
													column.render(
														typeof column.accessor === "function"
															? column.accessor(item)
															: item[column.accessor],
														item
													)
												)
												:
												(
													<Typography variant="textSM" color="var(--text-paragraph)">
														{ensureReactNode<T,I>(
															typeof column.accessor === "function"
																? column.accessor(item)
																: item[column.accessor]
														)}
													</Typography>
												)
											}
											</TableCell>
										)
									})
								}
							</TableRow>
						)
					})}
				</tbody>
			</StyledTable>
			{paginate && (
				<Pagination
					currentPage={currentPage}
					totalPages={totalPages}
					itemsPerPage={itemsPerPage}
					totalItems={data.length}
					onPageChange={setCurrentPage}
				/>
			)}
		</TableContainer>
	);
}


const ColumnHeaderSeg = styled(Flow)``;
